import type { CheckListItem } from './../../types/types.d';
import type { GemMeterElement, ScreenType, StandardState } from '../../types/types';

import _cloneDeep from 'lodash.clonedeep';

import {
  commitStandardList,
  commitElementGroup,
  getDataSetting,
  getShopableStandards,
  getConvertableStandard,
  getElementGroup,
  getElementFromStore,
  getStandardByRuleId,
  commitInitializeStatus,
  getIsInitialize,
  getElementsOfRules,
  getScreenType,
  getPageMetric,
  commitGroupLoading,
  commitInitialize,
  commitResetStore,
  getElementOfRule,
  getPageElements,
  isLandingPage,
  getPageRules,
  commitPageRules,
} from './useGemMeterStore';
import { $gryffEditor } from '@/app/index';
import CHECK_LIST, { SHOPABLE_PREFIX } from '../../constants/rulesConstants';
import { GEM_METER_TRIGGER_EVENT } from '../../constants';
import { isPagePublished } from './useGemMeterStore';
import emitter from '@/emitter';

import GemMeterHelper from './v2/GemMeterHelper';
import GemMeterBaseRule from '../rules/GemMeterBaseRule';
import captureError, { SCOPE } from '@/modules/common/utils/captureError';

// elementId != elementMerId
// elementId: 123456789
// elementMerId: #e-123456789

export const DEFAULT_REMOVE_ELEMENT_ID = '';

export const removeListeners = () => {
  emitter.off('store:add-snipper-finish');
  emitter.off('store:add-async-module-finish');
  emitter.off('gem-meter:element-removed');
};

const runTriggerGemMeter = (key: string) => {
  switch (key) {
    case GEM_METER_TRIGGER_EVENT.SAVE_PAGE:
      commitPageRules([CHECK_LIST.WEAK_SEO]);
      runPageValidation(DEFAULT_REMOVE_ELEMENT_ID, [CHECK_LIST.WEAK_SEO]);
      break;
    case GEM_METER_TRIGGER_EVENT.PAGE_SETTING_SEO: {
      const pageRulesStore = getPageRules();
      if (pageRulesStore.find((item) => item.id === CHECK_LIST.WEAK_SEO.id)) {
        runPageValidation(DEFAULT_REMOVE_ELEMENT_ID, [CHECK_LIST.WEAK_SEO]);
      }
      break;
    }
    case GEM_METER_TRIGGER_EVENT.PAGE_SETTING_LAZY_LOAD:
      // runPageValidation(DEFAULT_REMOVE_ELEMENT_ID, [CHECK_LIST.MISSING_LAZY_LOADING]);
      break;
    default:
      break;
  }
};

export const listeners = () => {
  if (isLandingPage()) {
    emitter.on('store:add-snippet-finish', (options) => {
      addElement(options.id, options.key);
    });

    emitter.on('store:add-async-module-finish', (options: any) => {
      /* Article List */
      /* Product List List */
      /* Related Products */

      handleAddAsyncModule(options);
    });

    emitter.on('gem-meter:element-removed', () => {
      GemMeterHelper.clearStore();
    });

    emitter.on('gem-meter:trigger-rules', (type: any) => {
      runTriggerGemMeter(type);
    });
  }
};

let timeout: any;

// Update to version 2
export async function init() {
  if (!isLandingPage()) {
    return;
  }

  const screenType = getScreenType();
  const isInitialize = getIsInitialize();
  if (isInitialize) {
    return;
  }

  commitResetStore();
  const dataSettings = getDataSetting();
  const elementIdRegex = /^[0-9]{13}.*$/;
  await Promise.all(
    Object.keys(dataSettings).map((key) => {
      if (!elementIdRegex.test(key)) {
        return false;
      }
      const element = JSON.parse(dataSettings[key as keyof typeof dataSettings]);

      return addElement(element.merId, element.key, true);
    }),
  );

  runPageValidation('');
  commitInitialize(true);
  commitInitializeStatus(screenType, true);
  removeListeners();
  listeners();
}

// Updated to version 2
export async function reRunInit() {
  if (!isLandingPage()) {
    return;
  }

  try {
    let shopableStandards = getShopableStandards();
    let convertableStandards = getConvertableStandard();
    const dataSettings = getDataSetting();
    const screenType = getScreenType();
    const elementIdRegex = /^[0-9]{13}.*$/;
    commitInitializeStatus(screenType, true);
    await Promise.all(
      Object.keys(dataSettings).map(async (key) => {
        if (!elementIdRegex.test(key)) {
          return false;
        }

        // Update element of rule
        const element = JSON.parse(dataSettings[key as keyof typeof dataSettings]);
        const elementsOfRules = getElementsOfRules();
        if (!elementsOfRules.hasOwnProperty(element.merId)) {
          return;
        }

        const isVisibility = await checkVisibilityOfElement(element.merId);
        if (isVisibility === false) {
          elementsOfRules[element.merId][screenType].visible = false;
        }

        const rules = GemMeterHelper.getRule(element.key);
        if (!rules) {
          return;
        }

        const elementGroups = getElementGroup();
        const selector = `${element.merId} ${rules.additionalSelector}`;
        let metric = getPageMetric();
        rules.handlers.forEach((handler) => {
          const ruleId = handler.ruleId;
          const gemElement = GemMeterHelper.createGemMeterElement(element.key, element.merId);

          // Update element group
          if (
            elementsOfRules.hasOwnProperty(element.merId) &&
            !elementsOfRules[element.merId][screenType].visible &&
            elementGroups[screenType].hasOwnProperty(ruleId)
          ) {
            delete elementGroups[screenType][ruleId][element.merId];
          } else if (
            elementsOfRules.hasOwnProperty(element.merId) &&
            elementsOfRules[element.merId][screenType].visible &&
            elementGroups[screenType].hasOwnProperty(ruleId) &&
            !elementGroups[screenType][ruleId].hasOwnProperty(element.merId)
          ) {
            elementGroups[screenType][ruleId][element.merId] = _cloneDeep(gemElement);
          }

          if (GemMeterHelper.isStandaloneRule(ruleId)) {
            handler.validate(`EDIT@${element.merId}`, screenType);
            return;
          }

          let isValid = null;
          if (elementsOfRules[element.merId][screenType].visible) {
            isValid = handler.validate(selector, screenType);
          }

          if (ruleId.includes(SHOPABLE_PREFIX)) {
            const { newStandards, newMetric } = GemMeterHelper.updateStoreWhenChangeScreen(
              shopableStandards,
              metric,
              ruleId,
              screenType,
              gemElement,
              isValid,
            );
            shopableStandards = newStandards;
            metric = newMetric;
          } else {
            const { newStandards, newMetric } = GemMeterHelper.updateStoreWhenChangeScreen(
              convertableStandards,
              metric,
              ruleId,
              screenType,
              gemElement,
              isValid,
            );
            convertableStandards = newStandards;
            metric = newMetric;
          }
        });
        return isVisibility;
      }),
    );
    runPageValidation('');
    commitStandardList('shopableStandards', shopableStandards);
    commitStandardList('convertableStandards', convertableStandards);

    clearTimeout(timeout);
    timeout = setTimeout(() => {
      emitter.emit('gem-meter:change-screen-type');
      commitGroupLoading(false);
    }, 100);
  } catch (error) {
    captureError(
      SCOPE.GEM_METER_SCOPE,
      'error',
      {
        function: 'reRunInit',
        screenType: getScreenType(),
      },
      error,
    );
  }
}

// Update to version 2
export async function addElement(elementMerId: string, elementKey: string, isInit?: boolean) {
  if (!isLandingPage()) {
    return;
  }

  try {
    // Skip this function when page is not finish initialize
    const isInitialize = getIsInitialize();
    if (!isInitialize && !isInit) {
      return;
    }

    const rules = GemMeterHelper.getRule(elementKey);
    const rulesGroup = GemMeterHelper.convertKeyToGroup(elementKey);
    const screenType = getScreenType();
    const isVisibility = await checkVisibilityOfElement(elementMerId);
    if (isVisibility !== null) {
      // Manual control which element added to page
      const pageElements = getPageElements();
      if (!pageElements.hasOwnProperty(elementMerId.substring(2))) {
        pageElements[elementMerId.substring(3)] = 0;
      }
    }

    if ((rules || rulesGroup) && isVisibility !== null) {
      const elementsOfRules = getElementsOfRules();
      if (!elementsOfRules.hasOwnProperty(elementMerId)) {
        // Add element to element of rules
        elementsOfRules[elementMerId] = GemMeterHelper.createElementInfo();
      }

      // Element not visible on screen
      if (!isVisibility) {
        elementsOfRules[elementMerId][screenType].visible = false;
      }
    }

    // Add element to group
    const ruleIds = GemMeterHelper.convertKeyToGroup(elementKey) || [];
    if (isVisibility && ruleIds.length > 0) {
      let elementGroups = getElementGroup();
      elementGroups = GemMeterHelper.updateElementGroupWhenAdd(elementGroups, screenType, elementKey, elementMerId);
    }

    // Run page rule
    if (!isInit) {
      runPageValidation('');
    }

    // Dont have any related rule for element key
    if (!rules || isVisibility === null) {
      return;
    }

    // Run rule and update standard detail
    const elementOfRule = getElementOfRule(elementMerId);
    let shopableStandards = getShopableStandards();
    let convertableStandards = getConvertableStandard();
    let metric = getPageMetric();
    const selector = `${elementMerId} ${rules.additionalSelector}`;
    rules.handlers.forEach((handler) => {
      const ruleId = handler.ruleId;
      const gemElement: GemMeterElement = GemMeterHelper.createGemMeterElement(elementKey, elementMerId);
      if (GemMeterHelper.isStandaloneRule(ruleId)) {
        handler.validate(`ADD@${elementMerId}`, screenType);

        return true;
      }

      let isValid = null;
      if (elementOfRule[screenType].visible) {
        isValid = handler.validate(selector, screenType);
      }
      if (ruleId.includes(SHOPABLE_PREFIX)) {
        const { newStandard, newMetric } = GemMeterHelper.updateStoreWhenAddElement(
          shopableStandards,
          metric,
          ruleId,
          screenType,
          isValid,
          gemElement,
        );
        shopableStandards = newStandard;
        metric = newMetric;
      } else {
        const { newStandard, newMetric } = GemMeterHelper.updateStoreWhenAddElement(
          convertableStandards,
          metric,
          ruleId,
          screenType,
          isValid,
          gemElement,
        );
        convertableStandards = newStandard;
        metric = newMetric;
      }
    });
    commitStandardList('shopableStandards', shopableStandards);
    commitStandardList('convertableStandards', convertableStandards);
  } catch (error) {
    captureError(
      SCOPE.GEM_METER_SCOPE,
      'error',
      {
        function: 'addElement',
        screenType: getScreenType(),
        elementKey: elementKey,
      },
      error,
    );
  }
}

// Updated to version 2
export function runPageValidation(removeElementMerId = DEFAULT_REMOVE_ELEMENT_ID, rules?: CheckListItem[]) {
  if (!isLandingPage()) {
    return;
  }

  try {
    const screenType = getScreenType();
    let shopableStandards = getShopableStandards();
    let convertableStandards = getConvertableStandard();
    let metric = getPageMetric();
    const isPublished = isPagePublished();
    let pageRules = [
      CHECK_LIST.NO_CALL_TO_ACTION_FOUND,
      CHECK_LIST.PAGE_HEIGHT_IS_TOO_LONG,
      CHECK_LIST.WEAK_SENSE_OF_TRUST,
      // CHECK_LIST.MISSING_LAZY_LOADING,
    ];
    const pageRulesStore = getPageRules();
    pageRules = pageRules.concat(pageRulesStore);
    const notWeakSEO = pageRules.find((item) => item.id === CHECK_LIST.WEAK_SEO.id);
    if (isPublished && !notWeakSEO) {
      pageRules = pageRules.concat(CHECK_LIST.WEAK_SEO);
    }
    if (rules) {
      pageRules = rules;
    }

    pageRules.forEach((rule) => {
      if (rule.devices && !rule.devices.includes(screenType)) {
        return;
      }

      const ruleId = rule.id;
      const handler = GemMeterHelper.getPageRuleById(ruleId);
      if (handler) {
        const isValid = handler.validate(screenType, removeElementMerId);

        if (ruleId.includes(SHOPABLE_PREFIX)) {
          const { newStandards, newMetric } = GemMeterHelper.updateStoreWhenRunPageRule(
            shopableStandards,
            metric,
            ruleId,
            screenType,
            isValid,
          );
          shopableStandards = newStandards;
          metric = newMetric;
        } else {
          const { newStandards, newMetric } = GemMeterHelper.updateStoreWhenRunPageRule(
            convertableStandards,
            metric,
            ruleId,
            screenType,
            isValid,
          );
          convertableStandards = newStandards;
          metric = newMetric;
        }
      }
    });
  } catch (error) {
    captureError(
      SCOPE.GEM_METER_SCOPE,
      'error',
      {
        function: 'runPageValidation',
        screenType: getScreenType(),
        isRemove: removeElementMerId ? true : false,
      },
      error,
    );
  }
}

// Updated to version 2
export function removeElement(elementId: string) {
  if (!isLandingPage()) {
    return;
  }

  try {
    const pageElements = getPageElements();
    // Special handler for row
    // elementId = elementMerId when remove element is row
    if (elementId.substring(0, 2) === '#r') {
      delete pageElements[elementId.substring(3)];
      return;
    } else {
      delete pageElements[elementId];
    }

    // Can not found element from store
    const rawElement = getElementFromStore(elementId);
    if (!rawElement) {
      return;
    }

    const element = JSON.parse(rawElement);
    const elementsOfRules = getElementsOfRules();
    if (!elementsOfRules.hasOwnProperty(element.merId)) {
      return;
    }

    // Remove element from element group
    const ruleIds = GemMeterHelper.convertKeyToGroup(element.key) || [];
    let elementGroups = getElementGroup();
    ruleIds.forEach((ruleId) => {
      elementGroups = GemMeterHelper.updateElementGroupWhenRemove(elementGroups, ruleId, element.merId);
      const products = GemMeterHelper.getProductAndProductListIdsInGroup(ruleId);
      products.forEach((product) => {
        elementGroups = GemMeterHelper.updateElementGroupWhenRemove(elementGroups, ruleId, product.merId);
      });
    });
    commitElementGroup(elementGroups);

    let metric = getPageMetric();
    let shopableStandards = getShopableStandards();
    let convertableStandards = getConvertableStandard();
    const rules = GemMeterHelper.getRule(element.key);
    if (rules) {
      const screenType = getScreenType();
      rules.handlers.forEach((handler) => {
        const ruleId = handler.ruleId;
        if (GemMeterHelper.isStandaloneRule(ruleId)) {
          handler.validate(`REMOVE@${element.merId}`, screenType);

          return;
        }

        if (ruleId.includes(SHOPABLE_PREFIX)) {
          const { newStandards, newMetric } = GemMeterHelper.updateStoreWhenRemoveElement(
            shopableStandards,
            metric,
            ruleId,
            element.merId,
          );
          shopableStandards = newStandards;
          metric = newMetric;
        } else {
          const { newStandards, newMetric } = GemMeterHelper.updateStoreWhenRemoveElement(
            convertableStandards,
            metric,
            ruleId,
            element.merId,
          );
          convertableStandards = newStandards;
          metric = newMetric;
        }
      });
    }

    // Remove element from element of rule in every screen
    delete elementsOfRules[element.merId];

    // Run page validation
    runPageValidation(element.merId);

    commitStandardList('shopableStandards', shopableStandards);
    commitStandardList('convertableStandards', convertableStandards);
    emitter.emit('gem-meter:remove-element', elementId);
  } catch (error) {
    captureError(
      SCOPE.GEM_METER_SCOPE,
      'error',
      {
        function: 'removeElement',
        screenType: getScreenType(),
        elementId,
      },
      error,
    );
  }
}

// Updated to version 2
export function editElement(elementId: string, ruleId: string, isChanged?: boolean) {
  if (!isLandingPage()) {
    return;
  }

  try {
    // Skip if element not found in store
    const rawElement = getElementFromStore(elementId);

    if (!rawElement) {
      return false;
    }
    // Rule ID not found in any standard
    let standards = getStandardByRuleId(ruleId);
    if (!standards.hasOwnProperty(ruleId)) {
      return false;
    }

    // Skip if element not visible on page
    const element = JSON.parse(rawElement);
    if (!element) {
      return false;
    }

    const screenType = getScreenType();
    const elementOfRules = getElementsOfRules();
    if (
      !elementOfRules.hasOwnProperty(element.merId) ||
      (elementOfRules[element.merId] && !elementOfRules[element.merId][screenType]?.visible)
    ) {
      return null;
    }
    // Skip the rest of process if rule run in standalone mode
    if (GemMeterHelper.isStandaloneRule(ruleId)) {
      GemMeterHelper.checkElementByRule(element, ruleId, true);

      return;
    }
    // Skip no valid result return after validate
    const isValid = GemMeterHelper.checkElementByRule(element, ruleId);
    if (isValid === null) {
      return;
    }
    // Run page rule
    runPageValidation('');
    // Element doesn't exists in error list before
    let metric = getPageMetric();
    if (!isValid && !standards[ruleId].screen[screenType].errorElements.hasOwnProperty(element.merId)) {
      const gemElement = GemMeterHelper.createGemMeterElement(element.key, element.merId);
      metric[screenType].fixed = metric[screenType].fixed - 1 >= 0 ? metric[screenType].fixed - 1 : 0;
      metric[screenType].errors += 1;
      standards[ruleId].screen[screenType].errorElements[element.merId] = gemElement;
      const standardKey = ruleId.includes(SHOPABLE_PREFIX) ? 'shopableStandards' : 'convertableStandards';
      commitStandardList(standardKey, standards);

      return isValid;
    }

    // Is valid and element does not exists on error list
    if (isValid && !standards[ruleId].screen[screenType].errorElements.hasOwnProperty(element.merId)) {
      return isValid;
    }

    const elementRecheckId = GemMeterHelper.getElementIdRecheck(elementId, ruleId);
    if (isChanged && elementRecheckId.length > 1) {
      elementRecheckId.forEach((itemId) => {
        const { newStandards, newMetric } = GemMeterHelper.updateStoreWhenEditElement(
          metric,
          standards,
          ruleId,
          screenType,
          itemId,
          isValid,
        );
        standards = newStandards;
        metric = newMetric;
      });
    } else {
      const { newStandards, newMetric } = GemMeterHelper.updateStoreWhenEditElement(
        metric,
        standards,
        ruleId,
        screenType,
        element.merId,
        isValid,
      );
      standards = newStandards;
      metric = newMetric;
    }

    const standardKey = ruleId.includes(SHOPABLE_PREFIX) ? 'shopableStandards' : 'convertableStandards';
    commitStandardList(standardKey, standards);

    return isValid;
  } catch (error) {
    captureError(
      SCOPE.GEM_METER_SCOPE,
      'error',
      {
        function: 'editElement',
        screenType: getScreenType(),
        elementId,
        ruleId,
        isChanged,
      },
      error,
    );
  }
}

// Updated to version 2
export function skipElement(elementId: string, ruleId: string) {
  if (!isLandingPage()) {
    return;
  }

  try {
    const rawElement = getElementFromStore(elementId);
    if (!rawElement) {
      return false;
    }

    const screenType = getScreenType();
    const metric = getPageMetric();
    const element = JSON.parse(rawElement);
    const standards = getStandardByRuleId(ruleId);

    if (GemMeterHelper.isStandaloneRule(ruleId)) {
      const groupLength = GemMeterHelper.getGroupLengthByRuleId(ruleId, screenType);
      if (groupLength > 0) {
        metric[screenType].skipped += groupLength;
        metric[screenType].errors -= groupLength;
        const errorElements = standards[ruleId].screen[screenType].errorElements;
        const keys = Object.keys(errorElements);
        keys.forEach((key) => {
          errorElements[key].status = 'skip';
          errorElements[key].visible = false;
        });
      }

      return true;
    }

    // Element is not in error list so can not be skip
    if (!standards[ruleId].screen[screenType].errorElements.hasOwnProperty(element.merId)) {
      return false;
    }

    standards[ruleId].screen[screenType].errorElements[element.merId].status = 'skip';
    standards[ruleId].screen[screenType].errorElements[element.merId].visible = false;
    metric[screenType].skipped += 1;
    metric[screenType].errors -= 1;
    const standardKey = ruleId.includes(SHOPABLE_PREFIX) ? 'shopableStandards' : 'convertableStandards';

    commitStandardList(standardKey, standards);

    return true;
  } catch (error) {
    captureError(
      SCOPE.GEM_METER_SCOPE,
      'error',
      {
        function: 'skipElement',
        screenType: getScreenType(),
        elementId,
        ruleId,
      },
      error,
    );
  }
}

// Updated to version 2
export function skipPageValidateRule(ruleId: string) {
  if (!isLandingPage()) {
    return;
  }

  try {
    const screenType = getScreenType();
    const standards = getStandardByRuleId(ruleId);
    const metric = getPageMetric();
    standards[ruleId].screen[screenType].status = 'skipped';
    metric[screenType].skipped += 1;
    metric[screenType].errors -= 1;
    const standardKey = ruleId.includes(SHOPABLE_PREFIX) ? 'shopableStandards' : 'convertableStandards';
    commitStandardList(standardKey, standards);
  } catch (error) {
    captureError(
      SCOPE.GEM_METER_SCOPE,
      'error',
      {
        function: 'skipPageValidateRule',
        screenType: getScreenType(),
        ruleId,
      },
      error,
    );
  }
}

// Updated to version 2
export function removeElementFromError(elementId: string, ruleId: string) {
  if (!isLandingPage()) {
    return;
  }

  try {
    const rawElement = getElementFromStore(elementId);
    if (!rawElement) {
      return false;
    }

    const element = JSON.parse(rawElement);
    const standards = getStandardByRuleId(ruleId);
    const screenType = getScreenType();
    if (!standards[ruleId].screen[screenType].errorElements.hasOwnProperty(element.merId)) {
      return false;
    }

    if (['fixed', 'skip'].includes(standards[ruleId].screen[screenType].errorElements[element.merId].status)) {
      delete standards[ruleId].screen[screenType].errorElements[element.merId];
      const standardKey = ruleId.includes(SHOPABLE_PREFIX) ? 'shopableStandards' : 'convertableStandards';
      commitStandardList(standardKey, standards);

      return true;
    }

    return false;
  } catch (error) {
    captureError(
      SCOPE.GEM_METER_SCOPE,
      'error',
      {
        function: 'removeElementFromError',
        screenType: getScreenType(),
        ruleId,
      },
      error,
    );
  }
}

// Updated to version 2
export function removeElementsFromError(ruleId: string) {
  if (!isLandingPage()) {
    return;
  }

  try {
    const screenType = getScreenType();
    const standards = getStandardByRuleId(ruleId);
    const errorElements = standards[ruleId].screen[screenType].errorElements;
    const elementKeys = Object.keys(errorElements);
    elementKeys.forEach((key) => {
      if (errorElements[key].status === 'fixed') {
        delete standards[ruleId].screen[screenType].errorElements[errorElements[key].merId];
      } else if (errorElements[key].status === 'skip') {
        standards[ruleId].screen[screenType].errorElements[errorElements[key].merId].visible = false;
      }
    });
  } catch (error) {
    captureError(
      SCOPE.GEM_METER_SCOPE,
      'error',
      {
        function: 'removeElementsFromError',
        screenType: getScreenType(),
        ruleId,
      },
      error,
    );
  }
}

export function removeElementsFromInconsistentError(ruleId: string) {
  if (!isLandingPage()) {
    return;
  }

  try {
    const screenType = getScreenType();
    const standards = getStandardByRuleId(ruleId);
    const errorElements = standards[ruleId].screen[screenType].errorElements;
    const elementKeys = Object.keys(errorElements);
    const metric = getPageMetric();
    metric[screenType].errors -= elementKeys.length;
    metric[screenType].fixed += elementKeys.length;
    standards[ruleId].screen[screenType].status = 'fixed';
    elementKeys.forEach((key) => {
      delete standards[ruleId].screen[screenType].errorElements[errorElements[key].merId];
    });
  } catch (error) {
    captureError(
      SCOPE.GEM_METER_SCOPE,
      'error',
      {
        function: 'removeElementsFromInconsistentError',
        screenType: getScreenType(),
        ruleId,
      },
      error,
    );
  }
}

// Updated to version 2
export function handleElementAttrChanged(elementId: string, attributeName: string) {
  if (!isLandingPage()) {
    return;
  }

  try {
    const ruleIds = GemMeterHelper.convertRuleByAttrName(attributeName);
    if (!ruleIds.length) {
      return;
    }

    ruleIds.forEach((ruleId) => {
      editElement(elementId, ruleId, true);
    });
  } catch (error) {
    captureError(
      SCOPE.GEM_METER_SCOPE,
      'error',
      {
        function: 'handleElementAttrChanged',
        screenType: getScreenType(),
        attributeName,
      },
      error,
    );
  }
}

// Need Aidan check
export function handleAddAsyncModule(options: { id: string }) {
  if (!isLandingPage()) {
    return;
  }

  try {
    const { id } = options;
    const $element = $gryffEditor().find(`#m-${id}`);
    const $module = $element.children('.module');
    const limit = isNaN(Number($module.attr('data-limit'))) ? 0 : Number($module.attr('data-limit'));
    const elementsOfModule: any[] = [];
    const dataSettings = getDataSetting();
    const keys = Object.keys(dataSettings);
    const elementIdRegex = /^[0-9]{13}.*$/;
    const regex = /-child(.*?)-/;
    for (let index = 1; index <= limit; index++) {
      keys.forEach((key) => {
        if (key.includes(`${id}-child${index}`) && elementIdRegex.test(key) && key.match(regex)) {
          elementsOfModule.push(`#m-${key}`);
        }
      });
    }

    const shopableStandards = getShopableStandards();
    const convertableStandards = getConvertableStandard();

    handleRemoveWhenChangeProductList(shopableStandards, elementsOfModule, id);
    handleRemoveWhenChangeProductList(convertableStandards, elementsOfModule, id);
    const shopableListChecked = getElementChecked(shopableStandards, id);
    const convertableListChecked = getElementChecked(convertableStandards, id);
    const listChecked = new Set([...shopableListChecked, ...convertableListChecked]);
    const listWillCreate = elementsOfModule.filter((item) => !Array.from(listChecked).includes(item));
    const elementObjects = listWillCreate.map((id) => {
      return JSON.parse(dataSettings[id.substring(3)]);
    });

    elementObjects.forEach((item) => {
      addElement(item.merId, item.key);
    });
  } catch (error) {
    captureError(
      SCOPE.GEM_METER_SCOPE,
      'error',
      {
        function: 'handleAddAsyncModule',
        screenType: getScreenType(),
        options,
      },
      error,
    );
  }
}

// Need Aidan check
const handleRemoveWhenChangeProductList = (standards: StandardState, elementsOfModule: any[], id: string) => {
  if (!isLandingPage()) {
    return;
  }

  try {
    const regex = /-child(.*?)-/;
    const ruleIds = Object.keys(standards);
    const metric = getPageMetric();
    ruleIds.forEach((ruleId) => {
      (['lg', 'md', 'sm', 'xs'] as ScreenType[]).forEach((screenType) => {
        const errorElements = standards[ruleId].screen[screenType].errorElements;
        // const item = standards[ruleId].errorElements[screen];
        const elementMerIds = Object.keys(errorElements);
        elementMerIds.forEach((elementMerId) => {
          if (elementMerIds.includes(`${id}-child`) && regex.test(elementMerId)) {
            if (!elementsOfModule.includes(`${elementMerId}`)) {
              const errorElement = standards[ruleId].screen[screenType].errorElements[elementMerId];
              if (errorElement.status === 'skip') {
                metric[screenType].skipped -= 1;
              } else if (errorElement.status === 'fixed') {
                metric[screenType].fixed -= 1;
              } else {
                metric[screenType].errors -= 1;
              }
              delete standards[ruleId].screen[screenType].errorElements[elementMerId];
            } else {
              editElement(elementMerId.substring(3), ruleId);
            }
          }
        });
      });
    });
  } catch (error) {
    captureError(
      SCOPE.GEM_METER_SCOPE,
      'error',
      {
        function: 'handleRemoveWhenChangeProductList',
        screenType: getScreenType(),
        id,
      },
      error,
    );
  }
};

// Need Aidan check
const getElementChecked = (standards: StandardState, id: string) => {
  const regex = /-child(.*?)-/;
  const ruleIds = Object.keys(standards);
  const listChecked: any[] = [];
  ruleIds.forEach((ruleId) => {
    (['lg', 'md', 'sm', 'xs'] as ScreenType[]).forEach((screenType) => {
      const errorElements = standards[ruleId].screen[screenType].errorElements;
      const errorElementMerId = Object.keys(errorElements);
      errorElementMerId.forEach((elementMerId) => {
        if (elementMerId.includes(`${id}-child`) && regex.test(elementMerId)) {
          listChecked.push(elementMerId);
        }
      });
    });
  });

  return listChecked;
};

// Updated to version 2
export function handleUpdateVisibility(sectionId: string, screenType: ScreenType, isDisplay: boolean) {
  if (!isLandingPage()) {
    return;
  }

  try {
    const isElement = sectionId.indexOf('e-') !== -1;
    if (isElement) {
      GemMeterHelper.updateElementVisibility(sectionId.substring(2), screenType, isDisplay, '#e');
      runPageValidation(sectionId);
    } else {
      const childElements = GemMeterBaseRule.getElements(`#${sectionId} .element-wrap`);
      if (childElements !== null) {
        childElements.forEach((element) => {
          GemMeterHelper.updateElementVisibility(element.id.substring(2), screenType, isDisplay, '#e');
        });
      }

      const childModules = GemMeterBaseRule.getElements(`#${sectionId} .module-wrap`);
      if (childModules !== null) {
        childModules.forEach((element) => {
          GemMeterHelper.updateElementVisibility(element.id.substring(2), screenType, isDisplay, '#m');
        });
      }

      const selfModule = GemMeterBaseRule.getElement(`#${sectionId}.module-wrap`);
      if (selfModule !== null) {
        GemMeterHelper.updateElementVisibility(selfModule.id.substring(2), screenType, isDisplay, '#m');
      }

      runPageValidation(sectionId);
    }
  } catch (error) {
    captureError(
      SCOPE.GEM_METER_SCOPE,
      'error',
      {
        function: 'handleUpdateVisibility',
        screenType,
        sectionId,
      },
      error,
    );
  }
}

export const checkVisibilityOfElement = async (elementMerId: string) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const domElement = GemMeterBaseRule.getElement(elementMerId);
      if (!domElement) {
        return resolve(null);
      }

      if (GemMeterHelper.isSpecialElemWhenCheckVisibility(domElement as HTMLElement)) {
        resolve(!!((domElement as HTMLElement).offsetWidth || (domElement as HTMLElement).offsetHeight));
      }

      resolve(!!((domElement as HTMLElement).offsetWidth && (domElement as HTMLElement).offsetHeight));
    }, 50);
  });
};

export function skipElementsFromInconsistentError(ruleId: string) {
  if (!isLandingPage()) {
    return;
  }

  try {
    const screenType = getScreenType();
    const standards = getStandardByRuleId(ruleId);
    const errorElements = standards[ruleId].screen[screenType].errorElements;
    standards[ruleId].screen[screenType].status = 'skipped';
    const elementKeys = Object.keys(errorElements);
    const metric = getPageMetric();
    metric[screenType].errors -= elementKeys.length;
    metric[screenType].skipped += elementKeys.length;
    elementKeys.forEach((key) => {
      delete standards[ruleId].screen[screenType].errorElements[errorElements[key].merId];
    });
  } catch (error) {
    captureError(
      SCOPE.GEM_METER_SCOPE,
      'error',
      {
        function: 'skipElementsFromInconsistentError',
        screenType: getScreenType(),
        ruleId,
      },
      error,
    );
  }
}
